import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import ImageGallery from "@/components/ImageGallery";
import Spin360Viewer from "@/components/Spin360Viewer";
import Model3DViewer from "@/components/Model3DViewer";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { SITE_URL } from "@/lib/brand";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [tab, setTab] = useState("photos");
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const soldLabel = settings?.archive?.badge_label || "Found a New Home";

  useEffect(() => {
    setTab("photos"); setQty(1);
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setP(false));
    api.get(`/products/${slug}/related`).then((r) => setRelated(r.data));
  }, [slug]);

  useEffect(() => {
    if (!p) return;
    // Page title & meta description
    document.title = p.seo_title || `${p.title} — Golden Moth Market`;
    const desc = p.seo_description || p.short_description || "";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement("meta"); metaDesc.setAttribute("name", "description"); document.head.appendChild(metaDesc); }
    metaDesc.setAttribute("content", desc);
    // OG
    const og = (prop, val) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    og("og:title", p.seo_title || p.title);
    og("og:description", desc);
    if (p.og_image || p.primary_image) og("og:image", resolveImg(p.og_image || p.primary_image));
  }, [p]);

  if (p === null) return <div className="text-center py-32 font-caption text-brass">Retrieving artefact…</div>;
  if (p === false) return <div className="text-center py-32"><p className="font-display text-3xl text-gold">Not found</p><Link to="/shop" className="btn-outline mt-6 inline-block">Back to Shop</Link></div>;

  const sold = p.product_status === "sold_out";
  const oneOfOne = p.inventory_type === "one_of_one";
  const has360 = (p.media_type === "360" || p.media_type === "mixed") && (p.spin_360_images?.length || 0) > 0;
  const has3D = (p.media_type === "3d" || p.media_type === "mixed") && !!p.model_3d_url;
  const effectivePrice = p.is_sale && p.sale_price ? p.sale_price : p.price;
  const compare = p.is_sale && p.sale_price ? p.price : p.compare_at_price;

  // Product JSON-LD (uses only saved data — nothing fabricated)
  const ldOffer = {
    "@type": "Offer",
    "price": effectivePrice.toFixed(2),
    "priceCurrency": "USD",
    "availability": sold ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
    "url": typeof window !== "undefined" ? SITE_URL + window.location.pathname : "",
  };
  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": p.title,
    "description": p.seo_description || p.short_description || p.full_description || "",
    "image": [resolveImg(p.primary_image), ...(p.images || []).slice(1).map(resolveImg)].filter(Boolean),
    ...(p.sku ? { "sku": p.sku } : {}),
    ...(p.gtin ? { "gtin": p.gtin } : {}),
    ...(p.mpn ? { "mpn": p.mpn } : {}),
    ...(p.brand ? { "brand": { "@type": "Brand", "name": p.brand } } : {}),
    "offers": ldOffer,
  };

  const maxQty = oneOfOne ? 1 : p.inventory_quantity;
  const handleAdd = () => {
    if (sold) return;
    // Send effective (sale) price into cart so pricing is consistent
    add({ ...p, price: effectivePrice }, qty);
    toast.success(`${p.title} added to your cabinet`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="product-detail">
      {/* Product structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} data-testid="product-jsonld" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Media */}
        <div className="space-y-4">
          {(has360 || has3D) && (
            <div className="flex gap-2">
              <button data-testid="tab-photos" onClick={() => setTab("photos")}
                className={`font-caption text-[10px] px-3 py-2 border ${tab === "photos" ? "border-gold text-gold" : "border-brass/40 text-parchment/70"}`}>Photographs</button>
              {has360 && <button data-testid="tab-360" onClick={() => setTab("360")}
                className={`font-caption text-[10px] px-3 py-2 border ${tab === "360" ? "border-gold text-gold" : "border-brass/40 text-parchment/70"}`}>360° View</button>}
              {has3D && <button data-testid="tab-3d" onClick={() => setTab("3d")}
                className={`font-caption text-[10px] px-3 py-2 border ${tab === "3d" ? "border-gold text-gold" : "border-brass/40 text-parchment/70"}`}>3D View</button>}
            </div>
          )}
          {tab === "photos" && <ImageGallery images={p.images} />}
          {tab === "360" && has360 && <Spin360Viewer images={p.spin_360_images} />}
          {tab === "3d" && has3D && <Model3DViewer src={p.model_3d_url} poster={p.model_3d_poster} />}
        </div>

        {/* Info */}
        <div className="space-y-6">
          {p.collection && <div className="font-caption text-xs text-brass">{p.collection.replace(/-/g, ' ')}</div>}
          <h1 className="font-display text-3xl sm:text-5xl text-gold leading-tight">{p.title}</h1>
          <div className="flex flex-wrap gap-2">
            {oneOfOne && !sold && <div className="text-[11px] font-caption text-gold border border-gold/60 px-3 py-1">Only One Available</div>}
            {p.is_new_arrival && !sold && <div className="text-[11px] font-caption text-forest bg-brass px-3 py-1">New Arrival</div>}
            {p.is_sale && !sold && <div className="text-[11px] font-caption text-parchment bg-oxblood px-3 py-1">On Sale</div>}
          </div>

          <div className="flex items-baseline gap-3">
            {sold ? (
              <span className="sold-stamp inline-block">{soldLabel}</span>
            ) : (
              <>
                <span className="font-display text-3xl text-gold">${effectivePrice.toFixed(2)}</span>
                {compare && compare > effectivePrice && <span className="text-parchment/50 line-through text-lg">${compare.toFixed(2)}</span>}
              </>
            )}
          </div>

          <p className="text-parchment/80 italic text-lg leading-relaxed">{p.short_description}</p>
          {p.full_description && <p className="text-parchment/75 leading-relaxed">{p.full_description}</p>}

          <div className="grid grid-cols-2 gap-4 text-sm border-y border-brass-soft py-4">
            {p.materials && <div><div className="font-caption text-[10px] text-brass mb-1">Materials</div><div className="text-parchment/80">{p.materials}</div></div>}
            {p.condition && <div><div className="font-caption text-[10px] text-brass mb-1">Condition</div><div className="text-parchment/80">{p.condition}</div></div>}
            {p.brand && <div><div className="font-caption text-[10px] text-brass mb-1">Brand</div><div className="text-parchment/80">{p.brand}</div></div>}
            {p.sku && <div><div className="font-caption text-[10px] text-brass mb-1">SKU</div><div className="text-parchment/80">{p.sku}</div></div>}
          </div>

          {sold ? (
            <div className="space-y-3">
              <p className="text-parchment/70 italic">This piece has already found its home. Explore similar finds below.</p>
              <Link to="/shop" className="btn-outline">Explore Similar Finds</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {!oneOfOne && (
                <div className="flex items-center gap-3">
                  <span className="font-caption text-xs text-brass">Quantity</span>
                  <div className="flex items-center border border-brass/40">
                    <button data-testid="pd-qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="p-2 text-parchment/80 hover:text-gold"><Minus size={14} /></button>
                    <span className="px-4 text-cream">{qty}</span>
                    <button data-testid="pd-qty-inc" onClick={() => setQty(Math.min(maxQty, qty + 1))} className="p-2 text-parchment/80 hover:text-gold"><Plus size={14} /></button>
                  </div>
                  <span className="text-xs text-parchment/50">{p.inventory_quantity} in the cabinet</span>
                </div>
              )}
              <button data-testid="add-to-cart-btn" onClick={handleAdd} className="btn-gold w-full sm:w-auto">Add to Cabinet</button>
              <p className="text-xs text-parchment/50 italic">Shipping calculated at checkout.</p>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-24">
          <h2 className="font-display text-2xl sm:text-3xl text-gold text-center mb-10">You Might Also Cherish</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
